// ABOUTME: Datagrid view listing all openapi keys for the active customer.
// ABOUTME: Row click opens the details sidepanel; "New" button opens the create modal.
import { Box, Tooltip, useTheme } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { tokens } from "../../../../../../theme";
import ButtonGroup from "../../../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import Text from "../../../../../../components/theme/text/Text";
import ShLabel from "../../../../../../components/theme/label/ShLabel";
import ShDataGrid2 from "../../../../../../components/theme/datagrid2/ShDataGrid2";
import ShTextSkeleton from "../../../../../../components/theme/skeleton/ShTextSkeleton";
import SidepanelLoader from '../../../../../../components/theme/sidepanel2/SidepanelLoader';
import MAddOpenapikey from "../../../../../../components/global/Modals/MAddOpenapikey";
import { useGetOpenapikeysQuery } from "../../../../../../newapi/openapikey/openapikeySlice";
import { selectUserPermission } from "../../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from "../../../../../../utils/permissions";

const SpOpenapikeyDetails = SidepanelLoader(() =>
    import("../../../../../../components/global/Sidepanels/SpOpenapikeyDetails/SpOpenapikeyDetails")
);

const formatDate = (iso, locale) => iso
    ? new Date(iso).toLocaleString(locale, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "—";

const GridActions = ({ hasCreatePermission, handleOpen }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    if (!hasCreatePermission) return <ButtonGroup version={2} />;
    return (
        <ButtonGroup version={2}>
            <Shbutton
                onClick={() => handleOpen(true)}
                className="group-btn"
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                variant="contained"
                endIcon={<AddIcon />}
            >
                {t("new_openapi_key")}
            </Shbutton>
        </ButtonGroup>
    );
};

const gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    hideSearch: true,
};

const OPENAPI_DOCS_URL = "https://openapi.stockhub.nl/fulfilment/docs";
const OPENAPI_BASE_URL = "https://openapi.stockhub.nl/fulfilment/";
const OPENAPI_WEBSHOP_MANUAL_URL = "https://support.stockhub.nl/hc/nl/articles/30958341796380-Koppelen-met-de-Stockhub-API";

const InfoBanner = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const labelColor = theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400];

    const bgColor = theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100];
    const borderColor = theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400];

    const linkSx = {
        color: labelColor,
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        "&:hover": { textDecoration: "underline" },
        wordBreak: "break-all",
    };
    const codeSx = { fontFamily: "monospace", fontSize: 13, color: labelColor, wordBreak: "break-all" };

    return (
        <Box
            sx={{
                position: "relative",
                display: "flex",
                margin: 3,
                marginBottom: 3,
                borderRadius: 5,
                background: bgColor,
                border: `1px solid ${borderColor}`,
                boxShadow: `0 4px 12px ${theme.palette.mode === "dark" ? "rgba(0,0,0,.25)" : "rgba(46,65,95,.08)"}`,
                "@media screen and (max-width: 63.9375em)": {
                    flexDirection: "column",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    padding: 2.5,
                    flex: 1,
                    alignItems: "flex-start",
                    minWidth: 0,
                }}
            >
                <InfoOutlinedIcon sx={{ color: labelColor, marginTop: 0.25 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, minWidth: 0 }}>
                    <Text bold>{t("openapi_info_title")}</Text>
                    <Text light>{t("openapi_info_text")}</Text>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            padding: 1.5,
                            borderRadius: 2,
                            background: colors.red[100],
                            alignItems: "flex-start",
                            marginTop: 1,
                        }}
                    >
                        <WarningAmberIcon sx={{ color: colors.red[400], marginTop: 0.25 }} />
                        <Text>{t("openapi_security_warning")}</Text>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    padding: 2.5,
                    flex: 1,
                    minWidth: 0,
                    borderLeft: `1px solid ${borderColor}`,
                    "@media screen and (max-width: 63.9375em)": {
                        borderLeft: "none",
                        borderTop: `1px solid ${borderColor}`,
                    },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        <Text light>{t("openapi_docs_label")}:</Text>
                        <Box component="a" href={OPENAPI_DOCS_URL} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                            {OPENAPI_DOCS_URL}
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        <Text light>{t("openapi_base_url_label")}:</Text>
                        <Box component="code" sx={codeSx}>{OPENAPI_BASE_URL}</Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        <Text light>{t("openapi_auth_label")}:</Text>
                        <Box component="code" sx={codeSx}>{t("openapi_auth_value")}</Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                        <Text light>{t("openapi_webshop_manual_label")}:</Text>
                        <Box component="a" href={OPENAPI_WEBSHOP_MANUAL_URL} target="_blank" rel="noopener noreferrer" sx={linkSx}>
                            {OPENAPI_WEBSHOP_MANUAL_URL}
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

const All = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();

    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState(false);
    const [openapikeyId, setOpenapikeyId] = useState();
    const idRef = useRef(openapikeyId);

    const hasCreatePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.WEBSHOPCREDENTIALS_MANAGE, UserPrivileges.SETTINGS_ALL])
    );

    const [addOpen, setAddOpen] = useState(false);

    const openDetails = (params) => {
        if (!params) return;
        const newId = parseInt(params.row.id);
        if (idRef.current !== newId) {
            if (!spLoading) setSpLoading(true);
            setOpenapikeyId(newId);
            idRef.current = newId;
        }
        setIsExtended(true);
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions,
            mobileActions: <GridActions hasCreatePermission={hasCreatePermission} handleOpen={() => setAddOpen(true)} />,
        });
    }, [props, hasCreatePermission]);

    const columns = [
        { field: "name", headerName: t("name"), flex: 2, sortable: false, renderCell: (params) => params.row.skeleton ? <ShTextSkeleton/> : params.value },
        { field: "createdAt", headerName: t("created"), flex: 1, sortable: false, renderCell: (params) => params.row.skeleton ? <ShTextSkeleton/> : formatDate(params.value, i18n.language) },
        { field: "lastUsedAt", headerName: t("last_used"), flex: 1, sortable: false, renderCell: (params) => params.row.skeleton ? <ShTextSkeleton/> : formatDate(params.value, i18n.language) },
        {
            field: "revokedAt",
            headerName: t("status"),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton/>;
                const revoked = !!params.value;
                return (
                    <ShLabel size={32} fitted palette="normal" variant={revoked ? "red" : "green"}>
                        {revoked ? t("revoked") : t("active")}
                    </ShLabel>
                );
            }
        },
        {
            field: "rights",
            headerName: t("rights"),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton/>;
                const rights = Array.isArray(params.value) ? params.value : [];
                return (
                    <Tooltip title={rights?.join(", ") || "—"}>
                        <Box><Text>{t("n_rights", { count: rights.length })}</Text></Box>
                    </Tooltip>
                );
            }
        },
    ];

    return (
        <Box sx={{ height: 1, "@media screen and (max-width: 47.9375em)": { paddingTop: 0, paddingX: 0 } }}>
            <InfoBanner />

            <ShDataGrid2
                key={theme.palette.mode}
                content={useGetOpenapikeysQuery}
                gridActions={<GridActions hasCreatePermission={hasCreatePermission} handleOpen={() => setAddOpen(true)} />}
                title={t("openapi_keys")}
                gridOptions={gridOptions}
                sortModel={{ field: "createdAt", type: "DESC" }}
                columns={columns}
                onRowClick={openDetails}
            />

            <MAddOpenapikey open={addOpen} handleClose={() => setAddOpen(false)} />

            {spLoading && (
                <SpOpenapikeyDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    openapikeyId={openapikeyId}
                />
            )}
        </Box>
    );
};

export default All;
